// src/hooks/useCostSimulatorSizing.ts
// Hook for managing sizing state in the Cost Simulator with auto-run and mirror functionality

import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useSizingStateV2, SCENARIO_PRESETS } from '@/hooks/useSizingStateV2';
import { useUnifiedSizing } from '@/sizing/useUnifiedSizing';
import { useCatalog } from '@/hooks/useCatalog';
import { usePersonaProfiles } from '@/hooks/usePersonaCatalog';
import { buildSizingRequestFromState } from '@/sizing/buildSizingRequestFromState';
import { sizingStore } from '@/stores/sizingStore';

export function useCostSimulatorSizing() {
  const localState = useSizingStateV2();
  const unified = useUnifiedSizing();
  const {
    catalog,
    isLoading: catalogLoading,
    catalogCommitted,
    catalogHash,
  } = useCatalog();
  const personaProfilesQuery = usePersonaProfiles();
  const personaProfiles = personaProfilesQuery.data ?? [];

  const hasRunRef = useRef(false);
  const lastRunHashRef = useRef<string | null>(null);
  const hasSeededRef = useRef(false);

  // Seed default personas if users array is empty
  useEffect(() => {
    if (hasSeededRef.current) return;
    if (!personaProfiles.length) return;
    if (localState.scenario.users.length > 0) {
      hasSeededRef.current = true;
      return;
    }

    // Seed default personas (same as useSizingEngineV2)
    const desired = [
      { id: 'data-analyst', pct: 50 },
      { id: 'email-drafter', pct: 30 },
      { id: 'quick-lookup', pct: 20 },
    ];

    desired.forEach(({ id, pct }) => {
      const profile = personaProfiles.find((p) => p.id === id);
      if (profile) {
        localState.addUser({
          uid: profile.id,
          id: profile.id,
          label: profile.display_name ?? profile.name ?? id,
          pct,
          profileId: profile.id,
          inTok: profile.input_mean ?? profile.prompt_tokens_per_turn ?? 400,
          outTok: profile.output_mean ?? profile.response_tokens_per_turn ?? 300,
          read_sec: profile.read_sec_default ?? 3,
          digest_sec: profile.think_sec_default ?? 5,
        });
      }
    });

    hasSeededRef.current = true;
  }, [personaProfiles, localState]);

  // Catalog is ready when we have models, throughput, personas, AND seeded users
  const catalogReady = Boolean(
    catalogCommitted &&
    !catalogLoading &&
    !personaProfilesQuery.isLoading &&
    catalog?.ready &&
    catalog?.models?.length &&
    catalog?.throughput?.length &&
    personaProfiles.length > 0 &&
    localState.scenario.users.length > 0
  );

  // Build a sizing request from local state
  const buildRequest = useCallback(() => {
    if (
      !catalogCommitted ||
      !catalogReady ||
      !catalog?.models?.length ||
      !catalog?.throughput?.length ||
      !personaProfiles.length ||
      localState.scenario.users.length === 0
    ) {
      return null;
    }

    return buildSizingRequestFromState({
      catalog,
      personaProfiles,
      tradeoffs: localState.scenario.tradeoffs,
      users: localState.scenario.users,
      advanced: localState.scenario.advanced,
      selectedModelId: localState.scenario.selectedModelId,
    });
  }, [catalog, personaProfiles, localState.scenario]);

  // Run sizing with current local state
  const runSizing = useCallback(() => {
    const request = buildRequest();
    if (request) {
      unified.compute(request);
    }
  }, [buildRequest, unified]);

  // Mirror configuration from the global sizing store
  const mirrorFromSizingPage = useCallback(() => {
    const storeState = sizingStore.getSnapshot();
    if (!storeState.request) return;

    const req = storeState.request;

    // Copy concurrent users
    if (req.baseConcurrentUsers) {
      localState.setTradeoff('concurrentUsers', req.baseConcurrentUsers);
    }

    // Copy turns
    if (req.conversationTurns) {
      localState.setTradeoff('turns', req.conversationTurns);
    }

    // Copy model
    if (req.model?.id) {
      localState.setSelectedModelId(req.model.id);
    }

    // Copy precision
    if (req.model?.precisionBits) {
      localState.setTradeoff('precision', req.model.precisionBits as 4 | 8 | 16 | 32);
    }

    // Copy latency settings
    if (req.latencySloMs) {
      localState.setAdvanced('latencyMs', req.latencySloMs);
      localState.setAdvanced('latencyTargetMs', req.latencySloMs);
    }

    // Note: We don't copy users/personas as that would be complex
    // The user can manually adjust if needed
  }, [localState]);

  // Auto-run sizing on mount if no existing snapshot
  useEffect(() => {
    if (hasRunRef.current && lastRunHashRef.current === catalogHash) return;
    if (!catalogReady || !catalogHash) return;

    // Check if we already have a snapshot
    const storeState = sizingStore.getSnapshot();
    if (storeState.snapshot) {
      // We have data, sync local state from it
      if (storeState.request) {
        mirrorFromSizingPage();
      }
      hasRunRef.current = true;
      lastRunHashRef.current = catalogHash;
      return;
    }

    // No snapshot - run with defaults
    const request = buildRequest();
    if (request) {
      unified.compute(request);
      hasRunRef.current = true;
      lastRunHashRef.current = catalogHash;
    }
  }, [catalogReady, catalogHash, buildRequest, unified, mirrorFromSizingPage]);

  // Computed values
  const totalPct = useMemo(() => {
    return localState.scenario.users.reduce((sum, u) => sum + (u.pct ?? 0), 0);
  }, [localState.scenario.users]);

  return {
    // Local state management
    scenario: localState.scenario,
    setTradeoff: localState.setTradeoff,
    setAdvanced: localState.setAdvanced,
    updateUser: localState.updateUser,
    removeUser: localState.removeUser,
    addUser: localState.addUser,
    setSelectedModelId: localState.setSelectedModelId,
    applyPreset: localState.applyPreset,
    resetScenario: localState.resetScenario,
    
    // Computed
    totalPct,
    warningUnderPct: totalPct < 100,
    blockedOverPct: totalPct > 100,
    
    // Unified sizing results (from global store)
    snapshot: unified.snapshot,
    loading: unified.loading,
    error: unified.error,
    
    // Actions
    runSizing,
    mirrorFromSizingPage,
    
    // Catalog data for controls
    catalog,
    catalogLoading,
    personaProfiles,
    
    // Presets
    presets: SCENARIO_PRESETS,
  };
}

export type UseCostSimulatorSizingReturn = ReturnType<typeof useCostSimulatorSizing>;
